import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import CreateArticle from "./pages/CreateArticle";
import Login from "./pages/Login";
import ArticleDetail from "./pages/ArticleDetail";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Navbar from "./components/Navbar";
import AuthGuard from "./components/AuthGuard";
import RecentPosts from "./pages/RecentPosts";
import ArticlesList from "./pages/ArticlesList";
import InterviewList from "./pages/InterviewList";
import BlogList from "./pages/BlogList";
import SpecialArticle from "./pages/SpecialArticle";
import ModelViewer from "./pages/ModelViewer";

// Room Pages
import EngineerRoom from "./pages/rooms/EngineerRoom";
import CreatorRoom from "./pages/rooms/CreatorRoom";
import ProducerRoom from "./pages/rooms/ProducerRoom";
import ServantRoom from "./pages/rooms/ServantRoom";
import InstructorRoom from "./pages/rooms/InstructorRoom";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isArticleDetail = location.pathname.startsWith('/articles/') || 
                        location.pathname.startsWith('/create-article') || 
                        location.pathname.startsWith('/edit-article');
  const isRoomPage = ['/engineer', '/creator', '/producer', '/servant', '/instructor'].includes(location.pathname);
  
  // 部屋ページ遷移時は常にスクロールトップへ
  React.useEffect(() => {
    if (isRoomPage) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname]);
  
  let className = '';
  
  if (isAdminRoute) {
    className = 'admin-content';
  } else if (isArticleDetail) {
    className = 'article-detail';
  } else if (isRoomPage) {
    className = ''; // Room pages handle their own layout
  } else if (location.pathname !== '/') {
    className = 'main-content';
  }
  
  return <main className={className}>{children}</main>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Navbar />
        <ContentWrapper>
          <Routes>
            <Route path="/" element={<Index />} />
            
            {/* Room Pages */}
            <Route path="/engineer" element={<EngineerRoom />} />
            <Route path="/creator" element={<CreatorRoom />} />
            <Route path="/producer" element={<ProducerRoom />} />
            <Route path="/servant" element={<ServantRoom />} />
            <Route path="/instructor" element={<InstructorRoom />} />
            
            <Route path="/recent-posts" element={<RecentPosts />} />
            <Route path="/articles-list" element={<ArticlesList />} />
            <Route path="/interview-list" element={<InterviewList />} />
            <Route path="/blog-list" element={<BlogList />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/model-viewer" element={<ModelViewer />} />
            <Route path="/articles/special/:slug" element={<SpecialArticle />} />
            <Route path="/articles/:id" element={<ArticleDetail />} />
            <Route
              path="/create-article"
              element={
                <AuthGuard>
                  <CreateArticle />
                </AuthGuard>
              }
            />
            <Route
              path="/create-special-article"
              element={
                <AuthGuard>
                  <CreateArticle isSpecial />
                </AuthGuard>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <AuthGuard>
                  <AdminDashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/edit-article/:id"
              element={
                <AuthGuard>
                  <CreateArticle />
                </AuthGuard>
              }
            />
            <Route
              path="/edit-special-article/:id"
              element={
                <AuthGuard>
                  <CreateArticle isSpecial />
                </AuthGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ContentWrapper>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
